import { useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AnalyticsSummaryResponse,
  HeatmapResponse,
  OmissionsResponse,
  OnaResponse,
} from '@/api/types';
import { buildOuTree, type OuNode } from '@/components/common/OuTree/ouTree';
import { useOuScope } from '@/hooks/useOuScope';
import { fmtScore } from '@/utils/analytics';
import {
  aggregateDirectlyAssigned,
  aggregateHeatmapByOu,
  mergeAggregateForNode,
  overallAverage,
  type OuScoreAggregate,
} from '@/utils/heatmapAggregation';
import { DimensionFilterNotice } from '../components/DimensionFilterNotice';
import { OrgTreeNode } from '../components/OrgTreeNode';
import { usePersonNameResolver } from '../hooks/useAnalyticsNames';
import type { OrgNode } from '@/types/analytics.types';

interface OnaTabProps {
  apiOna?: OnaResponse;
  apiOmissions?: OmissionsResponse;
  apiSummary?: AnalyticsSummaryResponse;
  /** Misma matriz que consume el mapa de calor: garantiza que el árbol y esa pestaña
   *  reporten idénticos participantes y evaluaciones por unidad. */
  apiHeatmap?: HeatmapResponse;
  /** Dimensión seleccionada en el filtro; `null` cuando el reporte cubre todas. */
  activeDimensionName?: string | null;
}

const PAGE_SIZE = 5;

/** Cuántas personas muestra el ranking de omisiones. */
const OMISSION_RANKING_SIZE = 5;

/** Umbrales sobre el número de encuestas omitidas: mientras más omite una persona, más crítico. */
function omissionColor(omittedCount: number): string {
  if (omittedCount >= 3) return 'var(--cp-danger-light)';
  if (omittedCount >= 1) return '#f59e0b';
  return '#10b981';
}

const MATURITY_LEGEND = [
  { range: '≤ 2.0', bg: '#fef2f2', text: '#991b1b', ring: '#fca5a5', labelKey: 'analytics.heatmap.legendCritical' },
  { range: '2.0–2.5', bg: '#fffbeb', text: '#92400e', ring: '#fcd34d', labelKey: 'analytics.heatmap.legendLow' },
  { range: '2.5–3.0', bg: '#fefce8', text: '#713f12', ring: '#fde047', labelKey: 'analytics.heatmap.legendMedium' },
  { range: '3.0–3.5', bg: '#ecfdf5', text: '#065f46', ring: '#6ee7b7', labelKey: 'analytics.heatmap.legendGood' },
  { range: '≥ 3.5',  bg: '#d1fae5', text: '#064e3b', ring: '#34d399', labelKey: 'analytics.heatmap.legendExcellent' },
];

export function OnaTab({
  apiOna,
  apiOmissions,
  apiSummary,
  apiHeatmap,
  activeDimensionName = null,
}: OnaTabProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [isHidingEmpty, setIsHidingEmpty] = useState(true);

  // Catálogo recortado al alcance del usuario: la red organizacional solo dibuja las OUs
  // que administra.
  const { scopedOuItems: ouItems } = useOuScope();
  const ouForest = useMemo<OuNode[]>(() => buildOuTree(ouItems), [ouItems]);

  const ouAggregates = useMemo(() => aggregateHeatmapByOu(apiHeatmap), [apiHeatmap]);
  const dimensionCount = apiHeatmap?.columns.length ?? 0;

  const directRowLabel = t('analytics.ona.directlyAssigned');

  // El árbol reporta las mismas cifras que el mapa de calor: participantes evaluados y
  // encuestas respondidas recibidas. Antes usaba la suma de `interactionCount` de las aristas,
  // que es otra métrica y por eso el total no coincidía con el resto de la analítica.
  const rootNodes = useMemo<OrgNode[]>(() => {
    function toOrgNode(node: OuNode): OrgNode {
      const merged = mergeAggregateForNode(node, ouAggregates, dimensionCount);
      const children = node.children.map(toOrgNode);

      // Las personas asignadas a la OU misma se muestran como un nodo aparte; de lo contrario
      // desaparecen al expandir, porque los hijos solo representan sub-unidades.
      const direct = aggregateDirectlyAssigned(node, ouAggregates, dimensionCount);
      if (children.length > 0 && direct.participantCount > 0) {
        children.unshift(toDirectNode(node.ou.id, direct));
      }

      return {
        id: node.ou.id,
        n: node.ou.name,
        t: 'OU',
        sc: overallAverage(merged) ?? 0,
        ev: merged.evaluationCount,
        p: merged.participantCount,
        children,
      };
    }

    function toDirectNode(parentOuId: string, aggregate: OuScoreAggregate): OrgNode {
      return {
        id: `${parentOuId}::directos`,
        n: directRowLabel,
        t: 'OU',
        sc: overallAverage(aggregate) ?? 0,
        ev: aggregate.evaluationCount,
        p: aggregate.participantCount,
        children: [],
      };
    }

    return ouForest.map(toOrgNode);
  }, [ouForest, ouAggregates, dimensionCount, directRowLabel]);

  const visibleRoots = useMemo(
    () => (isHidingEmpty ? rootNodes.filter((node) => (node.p ?? 0) > 0) : rootNodes),
    [rootNodes, isHidingEmpty],
  );
  const emptyRootCount = rootNodes.length - rootNodes.filter((node) => (node.p ?? 0) > 0).length;

  const totalPages = Math.max(1, Math.ceil(visibleRoots.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pagedRoots = visibleRoots.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  // Ranking de omisiones: las personas que más encuestas han omitido, de mayor a menor.
  const topOmitters = useMemo(
    () => (apiOmissions?.participants ?? [])
      .filter((participant) => participant.omittedCount > 0)
      .sort((first, second) => second.omittedCount - first.omittedCount)
      .slice(0, OMISSION_RANKING_SIZE),
    [apiOmissions],
  );

  const { resolvePersonName } = usePersonNameResolver(
    useMemo(() => topOmitters.map((participant) => participant.evaluatedId), [topOmitters]),
  );

  const omissionRanking = useMemo(
    () => topOmitters.map((participant) => ({
      id: participant.evaluatedId,
      name: resolvePersonName(participant.evaluatedId),
      value: participant.omittedCount,
    })),
    [topOmitters, resolvePersonName],
  );

  const maxOmissions = omissionRanking[0]?.value ?? 0;

  /** Mensaje que reemplaza al ranking mientras carga o cuando nadie omitió; null si hay filas. */
  const rankingStateMessage = useMemo(() => {
    if (apiOmissions === undefined) return t('common.loading');
    if (omissionRanking.length === 0) return t('analytics.noData');
    return null;
  }, [apiOmissions, omissionRanking, t]);

  // Personas conectadas en la red: quienes emitieron o recibieron al menos una evaluación.
  const connectedPeople = useMemo(() => {
    const peopleIds = new Set<string>();
    for (const edge of apiOna?.edges ?? []) {
      peopleIds.add(edge.evaluatorId);
      peopleIds.add(edge.evaluatedId);
    }
    return peopleIds.size;
  }, [apiOna]);

  const kpis = [
    {
      label: t('analytics.ona.avgScore'),
      value: apiSummary ? fmtScore(apiSummary.overallAverage) : '—',
      color: '#059669',
    },
    {
      label: t('analytics.ona.totalEvals'),
      value: (apiSummary?.totalEvaluations ?? 0).toLocaleString(),
      color: '#2563eb',
    },
    {
      label: t('analytics.ona.collaboratorsKpi'),
      value: connectedPeople.toLocaleString(),
      color: '#F08521',
    },
    {
      label: t('analytics.ona.highestOmission'),
      value: maxOmissions > 0 ? t('analytics.ona.omittedCount', { count: maxOmissions }) : '—',
      color: 'var(--cp-danger-light)',
    },
  ];

  return (
    <div className="au-card">
      <div className="au-card-header">
        <div>
          <div className="au-card-title">
            <i className="fas fa-project-diagram" aria-hidden="true" />
            {t('analytics.ona.title')}
          </div>
          <div className="au-card-sub">{t('analytics.ona.subtitle')}</div>
        </div>
      </div>

      <DimensionFilterNotice dimensionName={activeDimensionName} />

      <div className="ona2-kpis">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="ona2-kpi">
            <div className="ona2-kpi-label">{kpi.label}</div>
            <div className="ona2-kpi-value" style={{ color: kpi.color }}>{kpi.value}</div>
          </div>
        ))}
      </div>

      <div className="ona2-layout">
        <div className="ona2-tree-col">
          <div className="ona2-tree-toolbar">
            <span className="ona2-tree-hint">{t('analytics.ona.treeHint')}</span>
            {emptyRootCount > 0 && (
              <label className="hm-toggle-empty">
                <input
                  type="checkbox"
                  checked={isHidingEmpty}
                  onChange={(event) => { setIsHidingEmpty(event.target.checked); setPage(0); }}
                />
                {t('analytics.heatmap.hideEmpty', { count: emptyRootCount })}
              </label>
            )}
          </div>

          <div className="ona2-tree">
            {pagedRoots.length === 0 ? (
              <div className="ona2-ranking-empty">{t('analytics.noData')}</div>
            ) : pagedRoots.map((node) => (
              <OrgTreeNode key={node.id} node={node} depth={0} />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="ona2-pager">
              <span className="ona2-pager-info">
                {currentPage * PAGE_SIZE + 1}–
                {Math.min((currentPage + 1) * PAGE_SIZE, visibleRoots.length)} / {visibleRoots.length}
              </span>
              <div className="ona2-pager-btns">
                <button
                  type="button"
                  className="ona2-pager-btn"
                  disabled={currentPage === 0}
                  onClick={() => setPage(currentPage - 1)}
                  aria-label={t('common.back')}
                >
                  <i className="fas fa-chevron-left" aria-hidden="true" />
                </button>
                {Array.from({ length: totalPages }, (_unused, index) => (
                  <button
                    type="button"
                    key={`page-${index}`}
                    className={`ona2-pager-btn${index === currentPage ? ' active' : ''}`}
                    onClick={() => setPage(index)}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  type="button"
                  className="ona2-pager-btn"
                  disabled={currentPage >= totalPages - 1}
                  onClick={() => setPage(currentPage + 1)}
                  aria-label={t('common.next')}
                >
                  <i className="fas fa-chevron-right" aria-hidden="true" />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="ona2-sidebar">
          <h3 className="ona2-sidebar-title">{t('analytics.ona.omissionsRanking')}</h3>
          {/* Las omisiones se cuentan por encuesta completa, no por dimensión: el ranking es el
              mismo con o sin filtro de comportamiento. */}
          <DimensionFilterNotice
            dimensionName={activeDimensionName}
            messageKey="analytics.dimensionFilter.notApplicable"
          />
          <div className="ona2-ranking">
            {rankingStateMessage !== null ? (
              <span className="ona2-ranking-empty">{rankingStateMessage}</span>
            ) : (
              omissionRanking.map((item, index) => {
                const rankColor = omissionColor(item.value);
                const barWidth = maxOmissions > 0 ? (item.value / maxOmissions) * 100 : 0;
                return (
                  <div key={item.id} className="ona2-rank-item">
                    <div className="ona2-rank-top">
                      <div className="ona2-rank-left">
                        <span className="ona2-rank-num">{index + 1}</span>
                        <span className="ona2-rank-name">{item.name}</span>
                      </div>
                      <span className="ona2-rank-evals">
                        {t('analytics.ona.omittedCount', { count: item.value })}
                      </span>
                    </div>
                    <div className="ona2-ombar">
                      <div className="ona2-ombar-track">
                        <div
                          className="ona2-ombar-fill"
                          style={{ width: `${barWidth}%`, background: rankColor }}
                        />
                      </div>
                      <span
                        className="ona2-rank-value"
                        style={{ '--ona2-rank-color': rankColor } as CSSProperties}
                      >
                        {item.value}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="ona2-legend">
            <div className="ona2-legend-title">{t('analytics.ona.maturityCulture')}</div>
            {MATURITY_LEGEND.map((level) => (
              <div key={level.range} className="ona2-legend-item">
                <span
                  className="ona2-legend-dot"
                  style={{ background: level.bg, borderColor: level.ring, color: level.text }}
                />
                <span className="ona2-legend-label">{t(level.labelKey)}</span>
                <span className="ona2-legend-range">{level.range}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
